import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import T from '../theme';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import { Tag, Btn, SectionWrap, Divider, FAQRow, FooterCTA } from '../components/shared';
import { CTA_OPTIONS, SERVICES, PROCESS_STEPS, CASE_STUDIES, FAQS } from '../data';

const F = "'Plus Jakarta Sans', sans-serif";
const MONO = "'DM Mono', monospace";

/* ── HERO PANEL DATA ──────────────────────────────────────────────────────── */
const HERO_PANELS = {
  website: {
    scenarios: [
      {
        num: '1',
        quote: '"I\'m building a new website and don\'t know where to start."',
        context: 'You want to launch a website, but:',
        pain: [
          'Choosing the right template feels overwhelming',
          'You\'re unsure what features you actually need',
          'Everything feels cluttered or unfocused',
          'You don\'t want to overbuild',
        ],
        howWeHelp: 'We help you choose the right template, define key functionality, structure your content, and design a clear, usable website that fits your goals.',
      },
      {
        num: '2',
        quote: '"My website uses a template and doesn\'t engage users."',
        context: 'Your website works, but:',
        pain: [
          'It looks generic',
          'Users don\'t interact much',
          'The value isn\'t clear',
          'Conversions are low',
        ],
        howWeHelp: 'We review your site, identify usability and interaction issues, and improve layout, flow, and calls-to-action so your website feels intentional and effective.',
      },
    ],
    stats: [
      { num: '60%', label: 'of Users Don\'t Find the\nInformation', accent: 'Don\'t Find the\nInformation', suffix: 'they were looking for on a website' },
      { num: '70%', label: 'Online Business Fall', accent: 'Fall', suffix: 'Because of bad usability' },
    ],
    cta: { label: 'View Package Service', route: '/services' },
    fallback: {
      text: 'I have a mix bag of issues with my website, I need help with selecting a package',
      stat: { num: '81%', label: 'Consumers are willing to pay for Better Experience', accent: 'Better Experience' },
      cta: 'Book a Free Call',
    },
  },
  product: {
    scenarios: [
      {
        num: '3',
        quote: '"My product is already in use, but the experience feels outdated or clunky."',
        context: 'Your product or website:',
        pain: [
          'Is live and has users',
          'Feels outdated or inconsistent',
          'Has usability issues built up over time',
          'Needs modernization or accessibility improvements',
        ],
        howWeHelp: 'We review existing flows, improve usability, modernize the experience, and check accessibility — without disrupting what already works.',
      },
    ],
    stats: [
      { num: '1 in 5', label: 'Consumers Stopped Purchasing', accent: 'Stopped\nPurchasing', suffix: 'from a Company after a poor Experience' },
    ],
    cta: { label: 'View Package Service', route: '/services' },
    fallback: {
      text: 'I have a mix bag of issues with my website, I need help with selecting a package',
      stat: { num: '81%', label: 'Consumers are willing to pay for Better Experience', accent: 'Better Experience' },
      cta: 'Book a Free Call',
    },
  },
  startup: {
    scenarios: [
      {
        num: '4',
        quote: '"My startup is almost ready, but the UX isn\'t strong."',
        context: 'You have:',
        pain: [
          'Core features defined',
          'Components or early UI in place',
        ],
        context2: 'But:',
        pain2: [
          'The experience isn\'t smooth',
          'The flow isn\'t clear',
          'You\'re unsure if users will understand it',
        ],
        howWeHelp: 'We refine workflows, apply UX design with light user research, test key flows, and deliver a clear, usable MVP experience.',
      },
    ],
    stats: [
      { num: '90%', label: 'Startups Fail', accent: 'Fail', suffix: 'without proper User research' },
    ],
    cta: { label: 'View Package Service', route: '/services' },
    quote: '"Appropriately done user research generates a useful product which provides a purpose for its target customers"',
    userCentric: {
      header: 'Be user-centric, cause',
      stats: [
        { num: '75%', label: 'of companies think they are user-centric' },
        { num: '30%', label: 'of their customers agree' },
      ],
    },
  },
  career: {
    type: 'career',
    headline: 'Curious about a career in UX?',
    sub: 'Whether you\'re switching careers, a fresh graduate, or just exploring — here\'s what you\'re probably feeling:',
    scenarios: [
      {
        num: '1',
        quote: '"I don\'t know where to start learning UX."',
        context: 'You might have:',
        pain: [
          'Looked at courses but feel overwhelmed by choices',
          'No design background and unsure if you qualify',
          'Started but don\'t know what to build in a portfolio',
          'Heard about UX but not sure what the job actually is',
        ],
        howWeHelp: 'Our free workshop "Thinking like a Designer" walks you through the mindset, process, and reality of UX — no experience needed.',
      },
      {
        num: '2',
        quote: '"I\'ve learned some UX but can\'t land a job."',
        context: 'You\'ve probably:',
        pain: [
          'Completed a course or bootcamp',
          'Built 1–2 portfolio projects but get no callbacks',
          'Struggled to explain your process in interviews',
          'Applied to dozens of roles with no responses',
        ],
        howWeHelp: 'We review your portfolio, sharpen your case studies, and coach you on how to present your thinking so hiring managers take notice.',
      },
    ],
    stats: [
      { num: '1.2M+', label: 'UX jobs expected globally by 2030', accent: 'UX jobs', suffix: 'and the talent gap is still wide open' },
      { num: '3×', label: 'higher salary than average design roles', accent: 'higher salary', suffix: 'for skilled UX designers vs. general designers' },
    ],
    cta: { label: 'Join Free Workshop', route: '/contact' },
    workshopCard: {
      title: 'Thinking like a Designer',
      desc: 'A free workshop to help you understand what it takes to be a UX designer — the mindset, the process, and the career path.',
      details: ['Free · No prior experience needed', 'Live + recorded sessions available', 'Certificate of completion included'],
    },
  },
};

/* ── HOW WE CAN HELP DATA (per feedback redesign) ────────────────────────── */
/* `quote` is an array of segments: plain strings render as-is; */
/* { text, color } segments render in that color for emphasis. */
const HELP_ROWS = [
  {
    key: 'template',
    title: 'Building a new website',
    question: 'Not sure which template or features you need?',
    desc: 'We help you choose the right template, define key functionality, and create a clear, usable website from the start.',
    quote: [
      { text: '88%', color: '#FF6B6B' },
      { text: ' of online users ' },
      { text: "won't", color: '#FF6B6B' },
      { text: ' ' },
      { text: 'return', color: '#B69CFF' },
      { text: ' to a site after a bad experience' },
    ],
    source: { label: 'Forbes', url: 'https://www.forbes.com' },
    ctaLabel: 'Get help choosing a template',
  },
  {
    key: 'modernize',
    title: 'Modernizing a digital product',
    question: 'Your site is live, but feels generic or ineffective?',
    desc: 'We modernize the experience, improve usability, and refine design and content to give your product a distinct identity — without breaking what already works.',
    quote: [
      { text: '81%', color: T.aqua },
      { text: ' consumers are willing to ' },
      { text: 'pay', color: '#B69CFF' },
      { text: ' for better experience' },
    ],
    source: { label: 'Yahoo Finance', url: 'https://finance.yahoo.com' },
    ctaLabel: 'Modernize a digital product',
  },
  {
    key: 'mvp',
    title: 'Turning an Idea to MVP',
    question: 'You have a powerful idea but not sure how to get market ready?',
    desc: 'We help startups turn an idea into a minimum valuable product — not just viable, but genuinely useful to real users. Our process: refining flows, applying UX design, and running quick, lightweight testing with real users — so you validate before you build, without losing momentum.',
    quote: [
      { text: '17%', color: '#FF6B6B' },
      { text: ' Startups ' },
      { text: 'Fail', color: '#FF6B6B' },
      { text: ' due to a poor user experience' },
    ],
    source: { label: 'CB Insights', url: 'https://www.cbinsights.com' },
    ctaLabel: 'Get my MVP ready',
  },
];

/* ── HERO ─────────────────────────────────────────────────────────────────── */
function HeroBanner() {
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const TAB_IDS = ['website', 'product', 'startup', 'career'];
  const activeId = selected !== null ? TAB_IDS[selected] : null;
  const panel = activeId ? HERO_PANELS[activeId] : null;

  return (
    <>
    <div style={{ position: 'relative', overflow: 'hidden', background: T.bg, paddingTop: 72 }}>
      {/* Dot pattern */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.55, backgroundImage: `radial-gradient(${T.borderGray} 1.5px, transparent 1.5px)`, backgroundSize: '28px 28px', pointerEvents: 'none' }} />
      {/* Aqua glow */}
      <div style={{ position: 'absolute', top: '-10%', right: '-6%', width: 560, height: 560, borderRadius: '50%', background: `radial-gradient(circle, ${T.aquaGhost} 0%, transparent 68%)`, pointerEvents: 'none' }} />

      <div className="hero-content-grid" style={{ maxWidth: 1280, margin: '0 auto', padding: '88px 5% 80px', position: 'relative' }}>
        {/* Headline */}
        <div style={{ maxWidth: 800 }}>
          {/* <div className="hero-tag">
            <Tag style={{ marginBottom: 26 }}>Human-Centered UX · Affordable · Fast</Tag>
          </div> */}
          <h1 className="hero-h1" style={{ fontFamily: F, fontWeight: 800, fontSize: 'clamp(2.8rem, 6.5vw, 5rem)', lineHeight: 1.07, letterSpacing: '-2px', marginBottom: 28, color: T.ink }}>
            AI can build<br />your product<br />
            <span style={{ fontWeight: 400 }}>It <i>can't</i> understand<br />your users</span>
          </h1>
          <p className="hero-sub" style={{ fontSize: '1.1rem', color: T.muted, lineHeight: 1.8, maxWidth: 500 }}>
            We bring the human side to digital products, helping small businesses and startups create meaningful experiences users actually need.
          </p>
        </div>
      </div>
    </div>

    {/* "I'm here for to get help with" — its own section, blends into the expanded panel below */}
    <div style={{ position: 'relative', background: panel ? T.aquaGhost : T.surfaceAlt, transition: 'background 0.3s ease' }}>
      <div style={{ position: 'absolute', inset: 0, opacity: 0.4, backgroundImage: `radial-gradient(${T.borderGray} 1.5px, transparent 1.5px)`, backgroundSize: '28px 28px', pointerEvents: 'none' }} />
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '56px 5%', position: 'relative' }}>
        <div className="hero-chips" style={{ paddingBottom: panel ? 8 : 8 }}>
          <p style={{ fontFamily: MONO, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', color: T.slate, marginBottom: 14 }}>
            I'm here for to get help with
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {CTA_OPTIONS.map((o, i) => (
              <CtaChip key={o.id} opt={o} active={selected === i} onClick={() => setSelected(selected === i ? null : i)} />
            ))}
          </div>
        </div>
      </div>

      {/* Expandable panel — same background as the chip row above, no seam */}
      {panel && (
        <div key={activeId} style={{ position: 'relative', animation: 'panelSwitch 0.38s cubic-bezier(0.22,1,0.36,1) both' }}>
          <HeroPanel panel={panel} navigate={navigate} activeId={activeId} onClose={() => setSelected(null)} />
        </div>
      )}
    </div>
    </>
  );
}

function CtaChip({ opt, active, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 20px', borderRadius: 100, cursor: 'pointer',
        border: `1.5px solid ${active ? T.aqua : hov ? T.aqua : T.borderGray}`,
        background: active ? T.aquaGhost : hov ? T.aquaGhost2 : T.surface,
        transition: 'all 0.2s ease',
        boxShadow: active ? `0 0 0 3px ${T.aquaGhost}` : hov ? '0 2px 8px rgba(10,191,173,0.12)' : '0 1px 3px rgba(11,31,58,0.06)',
      }}>
      <Icon name={opt.icon} size={18} color={active ? T.aquaDark : hov ? T.aquaDark : T.inkMid} fill={active ? 1 : 0} />
      <span style={{ fontFamily: F, fontWeight: active ? 700 : 500, fontSize: '0.95rem', color: active ? T.aquaDark : hov ? T.aquaDark : T.inkMid, transition: 'color 0.2s' }}>
        {opt.label}
      </span>
    </button>
  );
}

/* ── HERO PANEL ───────────────────────────────────────────────────────────── */
function HeroPanel({ panel, navigate, activeId, onClose }) {
  if (activeId === 'career') {
    return (
      <div style={{ position: 'relative' }}>
        <button
          onClick={onClose}
          aria-label="Collapse section"
          className="panel-close-btn"
          style={{
            position: 'absolute', top: 24, right: '5%',
            width: 36, height: 36, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: T.surface, border: `1.5px solid ${T.borderGray}`,
            cursor: 'pointer', transition: 'all 0.2s ease', zIndex: 2,
          }}
        >
          <Icon name="close" size={18} color={T.ink} fill={0} />
        </button>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 5% 64px' }}>

          {/* Intro line */}
          <div style={{ marginBottom: 32 }}>
            <Tag style={{ marginBottom: 12 }}>UX Career Journey</Tag>
            <p style={{ fontSize: '1rem', color: T.muted, lineHeight: 1.8, maxWidth: 600 }}>{panel.sub}</p>
          </div>

          {/* Two scenario cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 24 }}>
            {panel.scenarios.map((sc, i) => (
              <ScenarioCard key={i} sc={sc} stat={panel.stats[i]} navigate={navigate} ctaLabel={panel.cta.label} ctaRoute={panel.cta.route} />
            ))}
          </div>

          {/* Workshop highlight card */}
          <div className="workshop-card" style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
            background: T.navy, border: `1.5px solid ${T.navy}`,
            borderRadius: 14, overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(11,31,58,0.18)',
          }}>
            <div className="workshop-card-left" style={{ padding: '40px 40px', borderRight: `1px solid rgba(255,255,255,0.08)` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: T.aquaGhost, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name="school" size={18} color={T.aquaDark} fill={1} />
                </div>
                <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: T.aqua, letterSpacing: '2px', textTransform: 'uppercase' }}>Free Workshop</span>
              </div>
              <h3 style={{ fontFamily: F, fontWeight: 800, fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', color: T.white, marginBottom: 14, lineHeight: 1.2 }}>
                {panel.workshopCard.title}
              </h3>
              <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, marginBottom: 28 }}>
                {panel.workshopCard.desc}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {panel.workshopCard.details.map((d, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>
                    <Icon name="check_circle" size={15} color={T.aqua} fill={1} />
                    {d}
                  </li>
                ))}
              </ul>
              <Btn onClick={() => { navigate(panel.cta.route); window.scrollTo(0, 0); }}>{panel.cta.label}</Btn>
            </div>
            <div style={{ padding: '40px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 32 }}>
              {panel.stats.map((s, i) => (
                <div key={i} style={{ borderBottom: 'none', paddingBottom: i === 0 ? 32 : 0 }}>
                  <div style={{ fontFamily: F, fontWeight: 800, fontSize: 'clamp(2.2rem, 4vw, 3.2rem)', color: T.aqua, letterSpacing: '-2px', lineHeight: 1, marginBottom: 8 }}>
                    {s.num}
                  </div>
                  <p style={{ fontFamily: F, fontWeight: 700, fontSize: '1.05rem', color: T.white, lineHeight: 1.35, marginBottom: 6 }}>
                    {s.label.split(s.accent)[0]}<span style={{ color: T.aqua }}>{s.accent}</span>{s.label.split(s.accent)[1]}
                  </p>
                  <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.6 }}>{s.suffix}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={onClose}
        aria-label="Collapse section"
        className="panel-close-btn"
        style={{
          position: 'absolute', top: 24, right: '5%',
          width: 36, height: 36, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: T.surface, border: `1.5px solid ${T.borderGray}`,
          cursor: 'pointer', transition: 'all 0.2s ease', zIndex: 2,
        }}
      >
        <Icon name="close" size={18} color={T.ink} fill={0} />
      </button>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 5% 64px' }}>

        {/* Main scenario cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: panel.fallback ? 20 : 0 }}>
          {panel.scenarios.map((sc, i) => (
            <ScenarioCard key={i} sc={sc} stat={panel.stats[i]} navigate={navigate} ctaLabel={panel.cta.label} ctaRoute={panel.cta.route} />
          ))}
        </div>

        {/* Startup-specific quote + user-centric block */}
        {activeId === 'startup' && panel.quote && (
          <>
            <div style={{ padding: '32px 0', borderTop: `1px solid ${T.borderGray}`, marginTop: 8 }}>
              <p style={{ fontFamily: F, fontWeight: 700, fontSize: 'clamp(1rem, 2vw, 1.25rem)', color: T.ink, lineHeight: 1.65, fontStyle: 'italic' }}>
                {panel.quote}
              </p>
            </div>
            <div style={{ background: T.surface, border: `1.5px solid ${T.borderGray}`, borderRadius: 14, padding: '36px 44px', boxShadow: '0 2px 12px rgba(11,31,58,0.06)' }}>
              <p style={{ fontFamily: MONO, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', color: T.slate, marginBottom: 28, textAlign: 'center' }}>
                {panel.userCentric.header}
              </p>
              <div className="usercentric-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                {panel.userCentric.stats.map((s, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: F, fontWeight: 800, fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', color: T.aquaDark, letterSpacing: '-2px', lineHeight: 1 }}>
                      {s.num}
                    </div>
                    <p style={{ fontSize: '1rem', color: T.ink, fontWeight: 600, marginTop: 10, lineHeight: 1.5 }}>{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Fallback / free call card */}
        {panel.fallback && (
          <ScenarioCard
            sc={{ isFallback: true, text: panel.fallback.text }}
            stat={panel.fallback.stat}
            navigate={navigate}
            ctaLabel={panel.fallback.cta}
            ctaRoute="/contact"
            isFallback
          />
        )}

      </div>
    </div>
  );
}

function ScenarioCard({ sc, stat, navigate, ctaLabel, ctaRoute, isFallback }) {
  const accentParts = stat ? stat.label.split(stat.accent) : [];

  return (
    <div className="scenario-card" style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 0,
      background: T.surface,
      border: `1.5px solid ${T.borderGray}`,
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: '0 2px 12px rgba(11,31,58,0.06)',
    }}>
      {/* Left — scenario detail */}
      <div className="scenario-card-left" style={{ padding: '36px 36px', borderRight: `1px solid ${T.borderGray}` }}>
        {isFallback ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: T.aquaGhost, border: `1.5px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="help_outline" size={20} color={T.aquaDark} fill={1} />
              </div>
              <p style={{ fontFamily: F, fontSize: '0.95rem', color: T.muted, lineHeight: 1.7 }}>{sc.text}</p>
            </div>
            <Btn onClick={() => { navigate(ctaRoute); window.scrollTo(0, 0); }}>{ctaLabel}</Btn>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <span style={{ fontFamily: MONO, fontSize: '0.65rem', color: T.aquaDark, background: T.aquaGhost, border: `1px solid ${T.border}`, borderRadius: 4, padding: '3px 8px' }}>
                {sc.num}
              </span>
            </div>
            <p style={{ fontFamily: F, fontWeight: 700, fontSize: '0.95rem', color: T.ink, marginBottom: 14, lineHeight: 1.5 }}>{sc.quote}</p>
            <p style={{ fontFamily: MONO, fontSize: '0.72rem', color: T.slate, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '1px' }}>{sc.context}</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sc.pain.map((p, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.88rem', color: T.muted }}>
                  <Icon name="remove" size={14} color={T.aquaDark} fill={1} style={{ marginTop: 3, flexShrink: 0 }} />
                  {p}
                </li>
              ))}
            </ul>
            {sc.context2 && (
              <>
                <p style={{ fontFamily: MONO, fontSize: '0.72rem', color: T.slate, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '1px' }}>{sc.context2}</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {sc.pain2.map((p, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.88rem', color: T.muted }}>
                      <Icon name="remove" size={14} color={T.aquaDark} fill={1} style={{ marginTop: 3, flexShrink: 0 }} />
                      {p}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div style={{ borderTop: `1px solid ${T.borderGray}`, paddingTop: 16, marginTop: 4 }}>
              <p style={{ fontFamily: F, fontWeight: 700, fontSize: '0.8rem', color: T.ink, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="lightbulb" size={14} color={T.aquaDark} fill={1} /> How we help
              </p>
              <p style={{ fontSize: '0.87rem', color: T.muted, lineHeight: 1.75 }}>{sc.howWeHelp}</p>
            </div>
          </>
        )}
      </div>

      {/* Right — stat */}
      {stat && (
        <div style={{ padding: '36px 36px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 16, background: T.navy }}>
          <div style={{ fontFamily: F, fontWeight: 800, fontSize: 'clamp(2.8rem, 5vw, 4rem)', color: T.aqua, letterSpacing: '-2px', lineHeight: 1 }}>
            {stat.num}
          </div>
          <p style={{ fontFamily: F, fontWeight: 800, fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)', color: T.white, lineHeight: 1.25, margin: 0 }}>
            {accentParts[0]}
            <span style={{ color: T.aqua }}>{stat.accent}</span>
            {accentParts[1]}
          </p>
          {stat.suffix && (
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, margin: 0 }}>{stat.suffix}</p>
          )}
          {!isFallback && (
            <div style={{ marginTop: 8 }}>
              <Btn onClick={() => { navigate(ctaRoute); window.scrollTo(0, 0); }}>{ctaLabel}</Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── ABOUT INTRO ──────────────────────────────────────────────────────────── */
function AboutIntro() {
 const navigate = useNavigate();

 const PILLS = [
 { icon: 'verified_user', label: 'Affordable' },
 { icon: 'bolt', label: 'Fast Turnaround' },
 { icon: 'groups', label: 'Human-Centered' },
 ];

 return (
 <div style={{ background: T.surfaceAlt }}>
 <SectionWrap>

 {/* Intro block — shaded card */}
 {/* <Reveal variant="up">
 <div className="intro-card" style={{
 background: T.surface,
 border: `1.5px solid ${T.borderGray}`,
 borderRadius: 16,
 padding: '40px 44px',
 boxShadow: '0 4px 24px rgba(11,31,58,0.07)',
 marginBottom: 64,
 display: 'grid',
 gridTemplateColumns: '1fr 1fr',
 gap: 48,
 alignItems: 'center',
 }}>
 <div>
 <Tag style={{ marginBottom: 16 }}>Design help for teams that need clarity</Tag>
 <p style={{ fontSize: '1rem', color: T.muted, lineHeight: 1.85, marginBottom: 24 }}>
 We help small businesses and early startups quickly improve their digital experience
 — through affordable UX reviews, workflow improvements, and fast implementation.
 </p>
 <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
 {PILLS.map(p => (
 <span key={p.label} style={{
 display: 'inline-flex', alignItems: 'center', gap: 6,
 background: T.aquaGhost, border: `1px solid ${T.border}`,
 borderRadius: 100, padding: '6px 14px',
 fontFamily: MONO, fontSize: '0.72rem', color: T.aquaDark,
 }}>
 <Icon name={p.icon} size={14} color={T.aquaDark} fill={1} />
 {p.label}
 </span>
 ))}
 </div>
 </div>
 <div className="intro-card-right" style={{
 borderLeft: `1px solid ${T.borderGray}`,
 paddingLeft: 48,
 }}>
 <Icon name="hub" size={32} color={T.aquaDark} fill={1} style={{ marginBottom: 16 }} />
 <p style={{
 fontFamily: F, fontWeight: 700,
 fontSize: 'clamp(1.1rem, 2vw, 1.35rem)',
 color: T.ink, lineHeight: 1.55,
 }}>
 We work with teams who already have something — but need clarity, focus, or better UX.
 </p>
 </div>
 </div>
 </Reveal> */}

 {/* How Can We Help header — plain heading, no icon badge (per feedback) */}
 <Reveal variant="up" delay="0.05s">
 <h2 style={{
 fontFamily: F, fontWeight: 800,
 fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
 letterSpacing: '-1px', color: T.ink, margin: '0 0 36px',
 }}>How Can We Help</h2>
 </Reveal>

 {/* Stacked full-width rows, each: left = title/desc, right = quote stat + CTA */}
 <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
 {HELP_ROWS.map((row, i) => (
 <Reveal key={row.title} variant="up" delay={`${i * 0.08}s`}>
 <HelpRow row={row} navigate={navigate} />
 </Reveal>
 ))}
 </div>

 </SectionWrap>
 </div>
 );
}

function HelpRow({ row, navigate }) {
 return (
 <div className="help-row" style={{
 display: 'grid',
 gridTemplateColumns: '1.2fr 1fr',
 gap: 0,
 background: T.surface,
 border: `1.5px solid ${T.borderGray}`,
 borderRadius: 12,
 overflow: 'hidden',
 boxShadow: '0 1px 4px rgba(11,31,58,0.06)',
 }}>
 {/* Left — title, question, description */}
 <div className="help-row-left" style={{ padding: '36px 40px', borderRight: `1px solid ${T.borderGray}` }}>
 <h3 style={{ fontFamily: F, fontWeight: 700, fontSize: '1.4rem', color: T.ink, marginBottom: 14 }}>
 {row.title}
 </h3>
 <p style={{ fontWeight: 600, fontSize: '0.92rem', color: T.ink, marginBottom: 12, fontFamily: F }}>
 {row.question}
 </p>
 <p style={{ fontSize: '0.92rem', color: T.muted, lineHeight: 1.8 }}>
 {row.desc}
 </p>
 </div>

 {/* Right — quote/stat + source + CTA, dark navy for contrast against left */}
 <div style={{ padding: '36px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 16, background: T.navy }}>
 <p style={{ fontFamily: F, fontWeight: 700, fontSize: '1.1rem', color: T.white, lineHeight: 1.5, margin: 0 }}>
 {row.quote.map((seg, i) => (
 <span key={i} style={seg.color ? { color: seg.color } : undefined}>{seg.text}</span>
 ))}
 </p>
 {row.source && (
 <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', margin: 0 }}>
 Source:{' '}
 <a href={row.source.url} target="_blank" rel="noopener noreferrer" style={{ color: T.aqua, textDecoration: 'underline' }}>
 {row.source.label}
 </a>
 </p>
 )}
 <div style={{ marginTop: 6 }}>
 <Btn onClick={() => { navigate(`/contact?service=${row.key}`); window.scrollTo(0, 0); }}>
 {row.ctaLabel}
 </Btn>
 </div>
 </div>
 </div>
 );
}

/* ── SERVICES PREVIEW ─────────────────────────────────────────────────────── */
function ServicesPreview() {
 const navigate = useNavigate();
 return (
 <SectionWrap style={{ background: T.bg }}>
 <Reveal variant="up">
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 64, flexWrap: 'wrap', gap: 20 }}>
 <div>
 <h2 style={{
 fontFamily: F, fontWeight: 800,
 fontSize: 'clamp(2.1rem, 4vw, 3.1rem)',
 letterSpacing: '-1px', lineHeight: 1.1, color: T.ink,
 }}>
 Featured Service, <span style={{ color: T.aquaDark }}>Affordable,</span><br />
 Quick Turn around
 </h2>
 </div>
 </div>
 </Reveal>

 <div className="services-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
 {SERVICES.slice(0, 3).map((svc, i) => (
 <Reveal key={svc.id} variant="up" delay={`${i * 0.1}s`}>
 <ServiceCard svc={svc} />
 </Reveal>
 ))}
 </div>
 </SectionWrap>
 );
}

function ServiceCard({ svc }) {
  const navigate = useNavigate();
 const [hov, setHov] = useState(false);
 return (
 <div
 onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
 onClick={() => { navigate('/services'); window.scrollTo(0, 0); }}
 style={{
 height: '100%',
 background: hov ? T.aquaGhost2 : T.surface,
 border: `1.5px solid ${hov ? T.aqua : T.borderGray}`,
 borderRadius: 12, padding: '28px 24px', cursor: 'pointer',
 transition: 'all 0.25s ease',
 transform: hov ? 'translateY(-4px)' : 'none',
 boxShadow: hov ? '0 12px 36px rgba(10,191,173,0.12)' : '0 1px 4px rgba(11,31,58,0.06)',
 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
 <span style={{ fontFamily: MONO, fontSize: '0.72rem', color: T.slate }}>{svc.number}</span>
 <Icon name={svc.icon} size={22} color={hov ? T.aquaDark : T.slate} fill={hov ? 1 : 0} />
 </div>
 <h3 style={{ fontFamily: F, fontWeight: 700, fontSize: '1.05rem', marginBottom: 8, color: T.ink }}>{svc.name}</h3>
 <p style={{ fontSize: '0.92rem', color: T.muted, marginBottom: 22, lineHeight: 1.7 }}>{svc.tagline}</p>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
 <span style={{ fontFamily: MONO, fontSize: '0.75rem', color: T.aquaDark }}>{svc.price}</span>
 <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: T.slate }}>{svc.time}</span>
 </div>
 </div>
 );
}

/* ── PROCESS PREVIEW ──────────────────────────────────────────────────────── */
const PROCESS_ICONS = ['phone_in_talk', 'description', 'manage_search', 'draw', 'rocket_launch'];

function ProcessPreview() {
 return (
 <div style={{ background: T.surfaceAlt }}>
 <SectionWrap>
 <div className="process-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 88, alignItems: 'start' }}>
 <Reveal variant="left" className="process-sticky" style={{ position: 'sticky', top: 100 }}>
 <Tag style={{ marginBottom: 14 }}>How it works</Tag>
 <h2 style={{
 fontFamily: F, fontWeight: 800,
 fontSize: '2.5rem', letterSpacing: '-1px', lineHeight: 1.12, marginBottom: 18, color: T.ink,
 }}>
 Clear steps.<br /><span style={{ color: T.aquaDark }}>No surprises.</span>
 </h2>
 <p style={{ fontSize: '1rem', color: T.muted, lineHeight: 1.85 }}>
 Every engagement starts with a free call and ends with a deliverable you can act on —
 or code we've already shipped.
 </p>
 </Reveal>
 <div>
 {PROCESS_STEPS.map((step, i) => (
 <Reveal key={step.number} variant="right" delay={`${i * 0.08}s`}>
 <ProcessRow step={step} icon={PROCESS_ICONS[i]} last={i === PROCESS_STEPS.length - 1} />
 </Reveal>
 ))}
 </div>
 </div>
 </SectionWrap>
 </div>
 );
}

function ProcessRow({ step, icon, last }) {
 const [hov, setHov] = useState(false);
 return (
 <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
 style={{ display: 'flex', gap: 24, paddingBottom: last ? 0 : 44, position: 'relative' }}>
 {!last && (
 <div style={{
 position: 'absolute', left: 21, top: 52, bottom: 0, width: 1,
 background: `linear-gradient(to bottom, ${T.aqua}, transparent)`, opacity: 0.35,
 }} />
 )}
 <div style={{
 flexShrink: 0, width: 44, height: 44, borderRadius: '50%',
 border: `1.5px solid ${hov ? T.aqua : T.borderGray}`,
 display: 'flex', alignItems: 'center', justifyContent: 'center',
 background: hov ? T.aquaGhost : T.surface,
 transition: 'all 0.22s ease',
 boxShadow: hov ? `0 0 0 4px ${T.aquaGhost}` : '0 1px 3px rgba(11,31,58,0.06)',
 }}>
 <Icon name={icon} size={20} color={hov ? T.aquaDark : T.slate} fill={hov ? 1 : 0} />
 </div>
 <div>
 <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
 <h3 style={{
 fontFamily: F, fontWeight: 700, fontSize: '1.1rem',
 color: hov ? T.aquaDark : T.ink, transition: 'color 0.2s',
 }}>{step.title}</h3>
 <span style={{
 fontFamily: MONO, fontSize: '0.68rem', color: T.aquaDark,
 background: T.aquaGhost, border: `1px solid ${T.border}`,
 borderRadius: 4, padding: '3px 10px',
 }}>{step.duration}</span>
 </div>
 <p style={{ fontSize: '0.95rem', color: T.muted, lineHeight: 1.8 }}>{step.desc}</p>
 </div>
 </div>
 );
}

/* ── WORK PREVIEW ─────────────────────────────────────────────────────────── */
const CASE_ICONS = { 'SaaS Product': 'devices', 'Service Business': 'storefront', 'Early Startup': 'rocket_launch' };

function WorkPreview() {
 const navigate = useNavigate();
 return (
 <SectionWrap style={{ background: T.bg }}>
 <Reveal variant="up">
 <div style={{ marginBottom: 64 }}>
 <Tag style={{ marginBottom: 14 }}>Case studies</Tag>
 <h2 style={{
 fontFamily: F, fontWeight: 800,
 fontSize: 'clamp(2.1rem, 4vw, 3.1rem)', letterSpacing: '-1px', color: T.ink,
 }}>
 Results that<br /><span style={{ color: T.aquaDark }}>speak for themselves.</span>
 </h2>
 </div>
 </Reveal>
 
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 22 }}>
 {CASE_STUDIES.map((cs, i) => (
 <Reveal key={cs.id} variant="up" delay={`${i * 0.12}s`}>
 <CaseCard cs={cs} />
 </Reveal>
 ))}
 </div>
 </SectionWrap>
 );
}

function CaseCard({ cs }) {
  const navigate = useNavigate();
 const [hov, setHov] = useState(false);
 return (
 <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
 onClick={() => { navigate('/work'); window.scrollTo(0, 0); }}
 style={{
 height: '100%',
 background: hov ? T.surfaceAlt : T.surface,
 border: `1.5px solid ${hov ? T.aqua : T.borderGray}`,
 borderRadius: 12, padding: '32px 28px', cursor: 'pointer',
 transition: 'all 0.25s ease',
 transform: hov ? 'translateY(-4px)' : 'none',
 boxShadow: hov ? '0 12px 36px rgba(10,191,173,0.1)' : '0 1px 4px rgba(11,31,58,0.06)',
 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
 <Icon name={CASE_ICONS[cs.tag] || 'work'} size={16} color={T.aquaDark} fill={1} />
 <span style={{ fontFamily: MONO, fontSize: '0.68rem', color: T.aquaDark, textTransform: 'uppercase', letterSpacing: '1.5px' }}>
 {cs.tag}
 </span>
 </div>
 <h3 style={{ fontFamily: F, fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.4, marginBottom: 14, color: T.ink }}>
 {cs.title}
 </h3>
 <p style={{ fontSize: '0.9rem', color: T.muted, lineHeight: 1.75, marginBottom: 24 }}>{cs.description}</p>
 <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
 {cs.metrics.map((m, i) => (
 <div key={i}>
 <div style={{ fontFamily: F, fontWeight: 800, fontSize: '1.35rem', color: i === 0 ? T.aquaDark : T.ink }}>{m.value}</div>
 <div style={{ fontFamily: MONO, fontSize: '0.65rem', color: T.slate }}>{m.label}</div>
 </div>
 ))}
 </div>
 </div>
 );
}

/* ── FAQ ──────────────────────────────────────────────────────────────────── */
function HomeFAQ() {
 return (
 <div style={{ background: T.surfaceAlt }}>
 <SectionWrap>
 <div className="faq-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 88 }}>
 <Reveal variant="left">
 <Tag style={{ marginBottom: 14 }}>FAQ</Tag>
 <h2 style={{ fontFamily: F, fontWeight: 800, fontSize: '2.3rem', letterSpacing: '-1px', lineHeight: 1.15, color: T.ink }}>
 Questions<br /><span style={{ color: T.aquaDark }}>we always get.</span>
 </h2>
 </Reveal>
 <Reveal variant="up" delay="0.1s">
 <div>
 {FAQS.map((faq, i) => <FAQRow key={i} faq={faq} />)}
 </div>
 </Reveal>
 </div>
 </SectionWrap>
 </div>
 );
}

/* ── UX CAREER WORKSHOP ───────────────────────────────────────────────────── */
function UXCareerWorkshop() {
 return (
 <SectionWrap style={{ background: T.bg }}>
 <Reveal variant="up">
 <h2 style={{
 fontFamily: F, fontWeight: 800,
 fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
 letterSpacing: '-1.5px', lineHeight: 1.1, color: T.ink,
 marginBottom: 40,
 }}>
 Curious About Pursuing<br />UX desiging as a Career?
 </h2>
 <p style={{ fontFamily: MONO, fontWeight: 700, fontSize: '0.9rem', color: T.ink, marginBottom: 16 }}>
 Enroll Our Free workshop
 </p>
 <h3 style={{
 fontFamily: F, fontWeight: 800,
 fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
 letterSpacing: '-2px', lineHeight: 1.05, color: T.ink, marginBottom: 18,
 }}>
 Thinking like a Designer
 </h3>
 <p style={{ fontSize: '1rem', color: T.muted, lineHeight: 1.75, marginBottom: 28 }}>
 Free workshop to help understand what it takes to be a UX designer
 </p>
 <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
 <Btn style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
 Register Now!
 </Btn>
 <span style={{
 display: 'inline-flex', alignItems: 'center',
 fontFamily: MONO, fontSize: '0.75rem', fontWeight: 700,
 textTransform: 'uppercase', letterSpacing: '1.5px',
 color: T.aquaDark, background: T.aquaGhost,
 border: `1px solid ${T.border}`, borderRadius: 100,
 padding: '8px 16px',
 }}>
 Coming Soon
 </span>
 </div>
 </Reveal>
 </SectionWrap>
 );
}

/* ── HOME ─────────────────────────────────────────────────────────────────── */
export default function Home() {
 return (
 <>
 <HeroBanner />
 <AboutIntro />
 <ServicesPreview />
 <Divider />
 <ProcessPreview />
 <Divider />
 {/* <WorkPreview /> */}
 <Divider />
 <HomeFAQ />
 <UXCareerWorkshop />
 <FooterCTA />
 </>
 );
}